// All facts on this page are drawn from rccdinc.com (Feb 2026).
// Where the public site doesn't disclose specifics, we use generic descriptors
// rather than inventing numbers or names.

export const SERVICES = [
  {
    id: 'entitlement',
    number: '01',
    title: 'Land Entitlement',
    tagline: 'Specific plans · zoning · approvals',
    description: 'RCCD\'s current focus. We author, co-author, and amend specific plans, navigate municipal entitlement processes, and shepherd raw land through to approved development rights.',
    image: 'https://images.pexels.com/photos/9716228/pexels-photo-9716228.jpeg',
    capabilities: ['Specific Plans', 'Zoning amendments', 'Tentative maps', 'Environmental (CEQA)'],
    span: 'col-span-12 lg:col-span-7 row-span-2',
  },
  {
    id: 'executive',
    number: '02',
    title: 'Executive Management',
    tagline: 'For builder & developer consortia',
    description: 'Ongoing executive management services for groups of builders and developers — most notably the New Model Colony consortium delivering 4,000 acres in the City of Ontario.',
    image: 'https://images.unsplash.com/photo-1508450859948-4e04fabaa4ea',
    capabilities: ['Consortium leadership', 'Schedule alignment', 'Infrastructure delivery'],
    span: 'col-span-12 lg:col-span-5',
  },
  {
    id: 'real-estate',
    number: '03',
    title: 'Real Estate & Homebuilding',
    tagline: 'Large-scale development background',
    description: '35+ years across land development, large-scale real estate, and homebuilding. Recent activity includes $350M in completed equity transactions and $50M in transactions pending.',
    image: 'https://images.unsplash.com/photo-1592385456792-dfef3ae5fa87',
    capabilities: ['Equity transactions', 'Acquisition support', 'Site assemblage'],
    span: 'col-span-12 lg:col-span-5',
  },
  {
    id: 'agency',
    number: '04',
    title: 'Agency Coordination',
    tagline: 'City & government relationships',
    description: 'Strong bonds with City Managers, Community Development Directors, and Planning, Engineering, Building, Finance, and Utilities departments across the Inland Empire.',
    image: 'https://images.unsplash.com/photo-1503708928676-1cb796a0891e',
    capabilities: ['City of Ontario', 'City of Chino', 'San Bernardino Co.', 'Riverside Co.'],
    span: 'col-span-12 lg:col-span-7',
  },
];

// Projects pulled directly from rccdinc.com/recent-projects — real named work
// with verifiable approval dates, lot counts, and tract map numbers.
export const PROJECTS = [
  {
    title: 'Grand Park',
    location: 'Ontario Ranch · City of Ontario',
    category: 'Residential',
    acres: '—',
    lots: '330 lots',
    year: 'Approved 2014',
    image: 'https://cloud-1de12d.becdn.net/media/iW=601&iH=368&oX=0&oY=0&cW=601&cH=368/3f6c3f5b5492a8a157049044e33ee4da/Pages+from+Grand+Park+-+Sale+Package+by+Hoffman+(9-23-15)-1920w.jpg',
    blurb: '330 lots in Ontario Ranch on Ontario Ranch Road between Haven and Archibald, just south of New Haven — with quick access to the I-15 via the Cantu-Galleano interchange. RCCD obtained all re-entitlement approvals: Grand Park Development Plan (Feb 2014), Specific Plan (Feb 2014), TTM 18662 (Sept 2014), EIR (Jan 2014), and recorded Development Agreement (Feb 2015). All wet-utility easements in place for off-site improvements.',
  },
  {
    title: 'Falloncrest Chino',
    location: 'Preserve Specific Plan · City of Chino',
    category: 'Residential',
    acres: '—',
    lots: '203 units',
    year: 'Approved 2021',
    image: 'https://images.unsplash.com/photo-1592385456792-dfef3ae5fa87',
    blurb: 'Highly visible site on Pine Avenue inside the Preserve Specific Plan, immediately adjacent to the actively-selling Harvest Masterplan. Easy access to the 71 Corona Expressway, 60 Freeway, and I-15 interchanges. RCCD secured the approved Specific Plan, EIR, and Development Agreement, plus TTM 19979 "A" Map, the 203-unit "B" Map (Feb 2021), and TTM 20312-2.',
  },
  {
    title: 'Avenue 229',
    location: 'The Avenue Specific Plan · New Model Colony',
    category: 'Residential',
    acres: '—',
    lots: '229 lots',
    year: 'Approved 2006–2014',
    image: 'https://cloud-1de12d.becdn.net/media/iW=601&iH=465&oX=0&oY=0&cW=601&cH=465/088649294d3b1c31cc230afeb537afde/Pages+from+Avenue+229+Ontario+Marketing+Package.jpg',
    blurb: 'A highly visible subdivision inside The Avenue Specific Plan in the New Model Colony — just south of Riverside Drive, abutting the Archibald Avenue corridor. Central to Chino and Ontario employment hubs, with freeway access to San Bernardino, Riverside, Los Angeles, and Orange Counties. RCCD obtained the EIR (Dec 2006), Specific Plan + amendments (2006–2014), the recorded Development Agreement, in-tract improvement plans, and TTM 18419 (229 approved lots).',
  },
  {
    title: 'Esperanza',
    location: 'Ontario Ranch · Joint Venture with GDC Communities',
    category: 'Residential',
    acres: '—',
    lots: '660 units',
    year: 'Approved',
    image: 'https://cloud-1de12d.becdn.net/media/iW=601&iH=695&oX=0&oY=10&cW=601&cH=591/ce80b2a33339ba83c53c43817a773bb9/Esperanza---Phasing-Plan.png',
    blurb: 'A 660-unit joint venture with GDC Communities in Ontario Ranch — thoughtfully planned residential site development with private amenities, parks, and direct access to nearby shopping, dining, and entertainment at Eastvale Gateway. Approved Specific Plan, EIR, and Development Agreement across 7 Planning Areas; TTMs 17931, 17932, 17933, 17935, 17936, 18876, and 17878.',
  },
  {
    title: 'New Model Colony',
    location: 'City of Ontario · 4,000 acres',
    category: 'Master-Planned',
    acres: '4,000',
    lots: '30,000+ residents',
    year: 'Ongoing',
    image: 'https://images.pexels.com/photos/9494281/pexels-photo-9494281.jpg',
    blurb: 'RCCD provides ongoing Executive Management Services for the NMC consortium of builders and developers. When complete, the 4,000-acre community will house over 30,000 residents alongside schools, shopping centers, a college, an office park, and industrial areas — anchored by the award-winning Mill Creek Regional Wetlands and supporting infrastructure built into the framework.',
  },
  {
    title: 'Five Adopted Specific Plans',
    location: 'Ontario & Chino · 2,200+ acres',
    category: 'Entitlement',
    acres: '2,200+',
    lots: '5 plans',
    year: 'Adopted',
    image: 'https://images.pexels.com/photos/9716228/pexels-photo-9716228.jpeg',
    blurb: 'RCCD has authored, co-authored, or amended five specific plans across the cities of Ontario and Chino — totalling more than 2,200 acres of approved development framework. Two additional plans for 125 acres are currently in process, with a pipeline of four further projects behind them.',
  },
];

export const STATS = [
  { value: 35, suffix: '+', label: 'Years of experience', desc: 'In land development, real estate, and homebuilding.' },
  { value: 350, prefix: '$', suffix: 'M+', label: 'Equity transactions', desc: 'Recently completed — $50M more pending.' },
  { value: 2200, suffix: '+', label: 'Acres in specific plans', desc: 'Across 5 plans in Ontario & Chino.' },
  { value: 4000, suffix: '', label: 'NMC acres managed', desc: 'New Model Colony, City of Ontario.' },
];

export const PROCESS = [
  { step: '01', title: 'Feasibility', body: 'Site analysis, jurisdictional review, and a candid read on whether the land is entitleable on the timeline and economics your team needs.' },
  { step: '02', title: 'Specific Plan', body: 'Authoring, co-authoring, or amending the specific plan that becomes the legal and design framework for the development — exactly the work RCCD has done five times in Ontario and Chino.' },
  { step: '03', title: 'Agency Coordination', body: 'Working shoulder-to-shoulder with City Managers, Community Development Directors, and Planning, Engineering, Building, Finance, and Utilities departments to keep the application moving.' },
  { step: '04', title: 'Public Hearings', body: 'Preparing the documentation, presentations, and stakeholder coordination required to take the plan through planning commission and council adoption.' },
  { step: '05', title: 'Executive Management', body: 'For consortia and large programs, RCCD continues on as the executive management layer — the role we play today inside the New Model Colony build-out.' },
];

// Geography expanded to all of Southern California (per rccdinc.com tagline).
export const REGIONS = [
  { id: 'la', name: 'Los Angeles Co.', projects: 'Active', acres: 'Multiple', x: 22, y: 38 },
  { id: 'oc', name: 'Orange Co.', projects: 'Anaheim HQ', acres: 'HQ', x: 32, y: 58 },
  { id: 'sb', name: 'San Bernardino Co.', projects: 'Active', acres: 'Multiple', x: 58, y: 30 },
  { id: 'rv', name: 'Riverside Co.', projects: 'Active', acres: 'Multiple', x: 56, y: 62 },
  { id: 'ontario', name: 'City of Ontario', projects: 'NMC + 4 plans', acres: '4,000+', x: 42, y: 42 },
  { id: 'chino', name: 'City of Chino', projects: 'Specific plans', acres: 'Multiple', x: 36, y: 56 },
];

// Notable outcomes from RCCD's public portfolio — third-person facts,
// not invented testimonials.
export const OUTCOMES = [
  {
    title: 'Mill Creek Regional Wetlands',
    detail: 'Award-winning regional wetlands facility delivered as part of the New Model Colony program.',
    label: 'NMC · Ontario',
  },
  {
    title: 'Tens of miles of regional infrastructure',
    detail: 'Storm drains, sewer lines, and regional streets — plus a bridge — built into the NMC framework.',
    label: 'NMC · Ontario',
  },
  {
    title: '8-school mitigation agreement',
    detail: 'A new elementary school and a fire station delivered as part of an 8-school mitigation program tied to NMC.',
    label: 'NMC · Ontario',
  },
  {
    title: '5 adopted specific plans',
    detail: '2,200+ acres of development framework authored, co-authored, or amended across Ontario and Chino.',
    label: 'Entitlement portfolio',
  },
];

// "How we work" — practice areas, not invented headshots.
export const PRACTICE = [
  {
    title: 'Specific Plan Authorship',
    detail: 'Five adopted plans in Ontario and Chino. Two active. A discipline RCCD has practiced for decades.',
  },
  {
    title: 'Executive Management',
    detail: 'Long-running engagement with the New Model Colony consortium — coordinating builders, agencies, and infrastructure delivery.',
  },
  {
    title: 'Agency Relationships',
    detail: 'Working bonds with City Managers, Community Development, Planning, Engineering, Building, Finance, and Utilities.',
  },
  {
    title: 'Capital Transactions',
    detail: '$350M completed and $50M pending in recent equity transactions on behalf of partners.',
  },
];

export const FAQS = [
  {
    q: 'Where does RCCD work?',
    a: 'RCCD\'s current and recent work spans Southern California — concentrated in the City of Ontario (including the 4,000-acre New Model Colony program), the City of Chino, and other locations across Riverside, San Bernardino, Los Angeles, and Orange Counties.',
  },
  {
    q: 'What does RCCD focus on today?',
    a: 'Land entitlement is RCCD\'s current focus — authoring specific plans, navigating agency processes, and coordinating approvals. RCCD also provides ongoing Executive Management Services for the New Model Colony consortium of builders and developers.',
  },
  {
    q: 'What is the New Model Colony?',
    a: 'The New Model Colony is an exclusive consortium of builders and developers collectively developing 4,000 acres in the City of Ontario. When complete, it will house over 30,000 residents along with schools, shopping centers, a college, an office park, and industrial areas.',
  },
  {
    q: 'What infrastructure has been delivered there?',
    a: 'Among other things, the NMC has constructed the award-winning Mill Creek Regional Wetlands, tens of miles of storm drains, sewer lines and regional streets, a bridge, a fire station, and a new elementary school as part of an 8-school mitigation agreement.',
  },
  {
    q: 'How many specific plans has RCCD authored?',
    a: 'RCCD has authored, co-authored, or amended five specific plans in the City of Ontario and the City of Chino — totalling more than 2,200 acres. Two additional specific plans for 125 acres are currently in process, with a pipeline of four further projects behind them.',
  },
  {
    q: 'How long has RCCD been doing this?',
    a: 'Over 35 years across land development, large-scale real estate, and homebuilding — with a current focus on entitlement and executive management.',
  },
];

// Real agency / city relationships RCCD names on its public About page.
export const PARTNERS = [
  'City of Ontario', 'City of Chino', 'San Bernardino County', 'Riverside County',
  'New Model Colony Consortium', 'City Managers', 'Community Development',
  'Planning Departments', 'Engineering', 'Building & Safety', 'Finance', 'Utilities',
];
